export interface IProductConfiguration {
    code: string;
    hasAPIKey?: boolean;
    renderWithSubscriptionPlan?: boolean;
    hideProgress?: boolean;
    hideProgressForAccountType?: 'ENTERPRISE' | 'TRIAL' | 'COMMUNITY';
    hideQuantity?: boolean;
    hideQuantityForAccountType?: 'ENTERPRISE' | 'TRIAL' | 'COMMUNITY';
    hideInHomepage?: boolean;
    hideInService?: boolean;
    type?: 'USER' | 'STANDARD' | 'SERVICE' | 'CONSUMPTION' | 'UNLIMITED';
    flavor?: 'NAMED' | 'MULTIUSER';
    showOnlyTotal?: boolean;
    /**
     * When set, the API Key displayed for this product is identified using this code rather than the actual product code.
     * E.g. for AI units the API Key code will be MLSERVICE_TIEMODEL, because if borrows the API key from Document Understanding
     */
    customAPIKeyCode?: string;
}

export interface IConsumptionPoolConfiguration {
    poolName: string;
    isBundled?: boolean;
}

export interface IEntitlementUsageConfiguration {
    entitlement: string;
    poolSummary?: IConsumptionPoolConfiguration[];
}

const SortedRobotsAndServicesProductConfigurations: IProductConfiguration[] = [
    { code: 'UNATT' },
    { code: 'UNATT-HOSTING' },
    { code: 'NONPR' },
    { code: 'TAUNATT' },
    { code: 'APPTESTR' },
    { code: 'AIW' },
    { code: 'AIG' },
    {
        code: 'DSU',
        renderWithSubscriptionPlan: true,
    },
    {
        code: 'TIE',
        hasAPIKey: true,
        hideProgressForAccountType: 'COMMUNITY',
        renderWithSubscriptionPlan: true,
        hideQuantityForAccountType: 'COMMUNITY',
    },
    {
        code: 'CV',
        hasAPIKey: true,
        hideProgress: true,
        showOnlyTotal: true,
    },
    { code: 'ACR' },
    { code: 'RU' },
    { code: 'AGU' },
    {
        code: 'AIU',
        hasAPIKey: true,
        customAPIKeyCode: 'TIE',
        hideInHomepage: false,
    },
    { code: 'HEAL' },
    { code: 'TEU' },
    { code: 'PLTU' },
    { code: 'PERFTEST' },
];

export const getSortedRobotsAndServicesProductConfigurations = () =>
    SortedRobotsAndServicesProductConfigurations.map(config => ({ ...config }));

const SortedUserBundleConfigurations: IProductConfiguration[] = [
    { code: 'ATTUNU' },
    {
        code: 'ATTUCU',
        flavor: 'MULTIUSER',
    },
    { code: 'CBNU' },
    { code: 'CTZDEVNU' },
    {
        code: 'CTZDEVCU',
        flavor: 'MULTIUSER',
    },
    { code: 'RPADEVNU' },
    {
        code: 'RPADEVCU',
        flavor: 'MULTIUSER',
    },
    { code: 'RPADEVPRONU' },
    {
        code: 'RPADEVPROCU',
        flavor: 'MULTIUSER',
    },
    { code: 'PRONU' },
    { code: 'PLUSNU' },
    { code: 'BASICNU' },
    { code: 'TSTDEVPRONU' },
    { code: 'TSTNU' },
    { code: 'APPTESTDEVNU' },
    { code: 'APPTESTNU' },
    { code: 'ACNU' },
    {
        code: 'ACCU',
        flavor: 'MULTIUSER',
    },
    { code: 'IDU' },
    { code: 'PMD' },
    { code: 'PMBU' },
    { code: 'AKIT' },
    { code: 'AXPNU' },
];
SortedUserBundleConfigurations.forEach(pc => (pc.type = 'USER'));

export const getSortedUserBundleConfigurations = () =>
    SortedUserBundleConfigurations.map(config => ({ ...config }));

const SortedEntitlementUsageConfigurations: IEntitlementUsageConfiguration[] = [
    {
        entitlement: 'APICALLS',
        poolSummary: [
            { poolName: 'ApiCalls.Purchased' },
            {
                poolName: 'ApiCalls.Bundled',
                isBundled: true,
            },
        ],
    },
];

export const getSortedEntitlementUsageConfigurations = () =>
    SortedEntitlementUsageConfigurations.map(config => ({
        ...config,
        poolSummary: config.poolSummary?.map(s => ({ ...s })),
    })) as IEntitlementUsageConfiguration[];

export const getAllProductsConfigurations = (includeUserProducts: boolean) => {
    const robotsAndServices = getSortedRobotsAndServicesProductConfigurations();
    if (!includeUserProducts) {
        return robotsAndServices;
    }
    const userProducts = getSortedUserBundleConfigurations();
    return userProducts.concat(robotsAndServices);
};
